import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Field, Textarea } from '@aions/ui-primitives';
import { apiFetch } from '../lib/auth-client';
import { bytesToBase64 } from '../lib/bytes';

type Voto = 'manter' | 'reformar' | 'reformar_parcial';

export function ParecerRecursoPage({
  caseId,
  onBack,
  onDone,
}: {
  caseId: string;
  onBack: () => void;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [voto, setVoto] = useState<Voto>('manter');
  const [justificativa, setJustificativa] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // Phase 5-MVP placeholder: justificativa "cifrada" é base64 do plaintext +
      // nonce zero. Phase 6 (admin onboarding + KEK_membro) substitui por
      // case-encrypt.encryptForRecurso(justificativa, dekRaw, caseId) usando
      // a DEK obtida via decryptRecursoCase. Mantém shape compatível com o port.
      const placeholderCipher = bytesToBase64(new TextEncoder().encode(justificativa));
      const placeholderNonce = bytesToBase64(new Uint8Array(12));

      const resp = await apiFetch(`/api/recurso/cases/${caseId}/parecer`, {
        method: 'POST',
        body: JSON.stringify({
          voto,
          justificativaPlaintext: placeholderCipher,
          nonce: placeholderNonce,
        }),
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? 'unknown');
        return;
      }
      onDone();
    } catch {
      setError('network');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} style={{ marginBottom: 16 }}>
        ← {t('case_detail.back')}
      </Button>
      <Card style={{ maxWidth: 720 }}>
        <h1>{t('parecer.title')}</h1>
        {error && (
          <Alert variant="error" style={{ marginBottom: 16 }}>
            {t(`parecer.errors.${error}`, { defaultValue: t('common.error_unknown') })}
          </Alert>
        )}
        <form onSubmit={(e) => void handleSubmit(e)} aria-label={t('parecer.title')}>
          <fieldset className="cf-field" role="radiogroup" aria-label={t('parecer.voto_label')}>
            <legend className="cf-field__label">{t('parecer.voto_label')}</legend>
            {(['manter', 'reformar', 'reformar_parcial'] as const).map((v) => (
              <label
                key={v}
                style={{ display: 'flex', gap: 10, padding: '8px 0', cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="voto"
                  value={v}
                  checked={voto === v}
                  onChange={() => setVoto(v)}
                />
                <span>{t(`parecer.votos.${v}`)}</span>
              </label>
            ))}
          </fieldset>

          <Field label={t('parecer.justificativa_label')}>
            {({ id }) => (
              <Textarea
                id={id}
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                minLength={50}
                required
                placeholder={t('parecer.justificativa_placeholder')}
              />
            )}
          </Field>

          <Button type="submit" loading={submitting}>
            {t('parecer.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
